<template>
	<div class="app-text-field">
		<label
			v-if="$slots.default"
			class="app-text-field__label"
		>
			<span
				v-if="$attrs.required"
				class="require-mark"
			>*</span>
			<slot></slot>
		</label>

		<div
			class="app-text-field__input-wrapper"
			:class="{'app-text-field__input-wrapper--disabled' : $attrs.disabled,
					'app-text-field__input-wrapper--error' : hasError}"
		>
			<AppIcon
				v-if="iconLeft"
				:icon-name="iconLeft"
				icon-color="#778395"
				class="app-text-field__icon app-text-field__icon--left"
			/>

			<input
				class="app-text-field__input"
				v-bind="$attrs"
				v-on="inputListener"
				ref="inputField"
			>

			<label
				v-if="$attrs.type === 'password'"
				class="app-text-field__toggle"
			>
				<input
					type="checkbox"
					@change="togglePassword"
				>
				{{ showPassword ? 'Hide' : 'Show'}}
			</label>

			<AppIcon
				v-if="showSuccess && !error"
				class="app-text-field__icon app-text-field__icon--right"
				icon-name="check"
				icon-color="#2dc4b6"
			/>

			<AppIcon
				v-if="iconRight"
				:icon-name="iconRight"
				icon-color="#778395"
				class="app-text-field__icon app-text-field__icon--right"
			/>
		</div>

		<p
			v-if="$attrs.maxlength && !hideCount"
			class="app-text-field__max"
		>
			{{ count }}
		</p>

		<p
			v-if="hasError"
			class="app-text-field__error"
		>
			{{ invalidMessage || error }}
		</p>
	</div>
</template>

<script>
export default {
	inheritAttrs: false,
	props: {
		error: String,
		iconLeft: { default: null },
		iconRight: { default: null },
		hideCount: Boolean
	},

	data () {
		return {
			showSuccess: false,
			invalidMessage: null,
			showPassword: false
		}
	},

	activated () {
		this.validate(this.$refs.inputField)
	},

	computed: {
		hasError () {
			return (this.error || this.invalidMessage)
		},

		inputListener () {
			return {
				...this.$listeners,
				invalid: this.setInvalidMessage,
				keyup: event => this.validate(event.target),
				input: event => this.$emit('input', event.target.value)
			}
		},

		count () {
			if (!this.$attrs.maxlength) return ''
			if (!this.$attrs.value) return this.$attrs.maxlength
			return Number(this.$attrs.maxlength) - Number(this.$attrs.value.length)
		}
	},

	methods: {
		togglePassword (event) {
			const inputField = this.$refs.inputField
			if (!event.target.checked) {
				inputField.type = 'password'
				this.showPassword = false
			} else {
				inputField.type = 'text'
				this.showPassword = true
			}
		},

		validate (target) {
			this.invalidMessage = null
			const typeToCheck = target.type !== 'text' && target.type !== 'password'
			const mismatch = target.validity.typeMismatch || target.validity.patternMismatch
			this.showSuccess = (typeToCheck || target.pattern) && target.value && !mismatch
		},

		setInvalidMessage (event) {
			event.preventDefault()
			if (event.target.validity.valueMissing) {
				this.invalidMessage = this.$t('error_fill_up_field')
			} else {
				this.invalidMessage = this.$attrs.type === 'email' ? this.$t('error_invalid_email') : this.$t('error_invalid_input')
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.app-text-field {
	display: inline-block;
	position: relative;
	font-size: 1em;

	&__label, &__input {
		font-weight: normal;
		font-family: inherit;
		font-size: inherit;
		display: block;
		width: 100%;
	}

	&__label {
		margin-bottom: .5em;

		.require-mark {
			color: $danger;
		}
	}

	&__input {
		outline: none;
		border: none;
		color: $default;
		background-color: #fff;
		flex: 1 1 auto;

		&::placeholder {
			color: $default-light;
			font-size: 1em;
		}

		&:focus {
			caret-color: $primary;
		}

		&:disabled {
			pointer-events: none;

			~ .app-text-field__toggle,
			~ .app-text-field__error {
				pointer-events: none;
			}
		}
	}

	&__icon {
		width: 1em;
		max-height: 16px;
		max-width: 16px;
		pointer-events: none;

		&--left {
			margin-right: 8px;
		}

		&--right {
			margin-left: 8px;
		}
	}

	&__toggle {
		flex-shrink: 0;
		margin-left: 8px;
		color: $default-light;
		user-select: none;
		cursor: pointer;
		font-size: 1em;

		input[type=checkbox] {
			position: absolute;
			appearance: none;
			outline: none;
			opacity: 0;
		}

		&:hover {
			color: $primary;
		}
	}

	&__input-wrapper {
		width: 100%;
		display: flex;
		align-items: center;
		position: relative;
		border: 1px solid $default-light;
		padding: 5px 8px;
		border-radius: 3px;
		background-color: #fff;
		color: $default;

		&:hover, &:focus-within {
			border-color: $primary;
		}

		&--disabled {
			pointer-events: none;
			opacity: 0.5;
		}

		&--error {
			border-color: $danger;

			&:hover, &:focus-within {
				border-color: $danger;
			}

			.app-text-field__toggle:hover {
				color: $danger;
			}
		}
	}

	&__max {
		margin-top: .5em;
		text-align: right;
		color: $default-light;
	}

	&__error {
		color: $danger;
		margin-top: .5em;
		display: flex;
		justify-content: flex-end;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/app-components/AppTextField.vue